import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import WindowLocation from 'helpers/windowLocation'
import Environment from 'helpers/environment'

import Api from 'services/api'
import type { Session } from 'types/Session'

const SessionContext = createContext<any>(null) // eslint-disable-line @typescript-eslint/no-explicit-any

export const SessionContextProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const navigate = useNavigate()

  const [session, setSession] = useState<Session | null>(null)
  const [isUpgraded, setIsUpgraded] = useState(false)
  const [isSessionLoaded, setIsSessionLoaded] = useState(false)

  const tierUpgraded = (oldSession: Session | null, newSession: Session | null) => {
    if (!oldSession || !newSession) return false

    const oldTier = oldSession.dealerPlan?.tier
    const newTier = newSession.dealerPlan?.tier
    const inTrial = newSession.dealerPlan?.in_trial

    return oldTier === 'standard' && newTier === 'premium' && inTrial === true
  }

  useEffect(() => {
    Api.Session.get()
      .then((res) => {
        if (JSON.stringify(session) !== JSON.stringify(res)) {
          setSession(res)
          setIsSessionLoaded(true)
          if (tierUpgraded(session, res)) {
            setIsUpgraded(true)
          }
        }
      })
      .catch((err) => {
        if (err.message === 'Unauthorized' || err.message.match(/Server Error/)) {
          if (Environment.isAWSCognito()) {
            navigate('/sign_in_error', { replace: true })
          } else {
            WindowLocation.replace(`${Environment.backendApiURL()}/api/sessions/saml/redirect?path=${encodeURIComponent(location.pathname)}&search=${encodeURIComponent(location.search)}`)
          }
        }

        setIsUpgraded(false)
        setSession(null)
        setIsSessionLoaded(false)
      })
  }, [location.pathname, location.search, session, navigate, isUpgraded])

  return (
    <SessionContext.Provider value={{ session, isSessionLoaded, setIsSessionLoaded, setSession, isUpgraded }}>
      {children}
    </SessionContext.Provider>
  )
}

export const useSessionContext = () => useContext(SessionContext)
